<template>
  <div class="in-review-shipments-table-row-component">
    <div class="body-item-row">
      <div class="body-item-1 flex items-center gap-2">
        <Tooltip v-if="getNotProcessedServiceTranslation" :text="getNotProcessedServiceTranslation" trigger="hover">
          <i class="fa-solid fa-arrows-rotate text-brand-primary text-xl" />
        </Tooltip>

        <div class="flex flex-col">
          <span class="font-medium pb-1">{{ shipment.source }}</span>
          <CopyComponent :text="shipment.source_tracking" />
        </div>
      </div>
      <div class="body-item-2">
        <div class="flex flex-col">
          <span class="text-text1 dark:text-dmText1">
            {{ `${shipment.weight} ${t('site_pro.lb')}` }}
          </span>
          <span class="text-xs text-text2 dark:text-dmText2">
            {{ `${shipment.width} x ${shipment.height} x ${shipment.length} ${t('site_pro.inches')}` }}
          </span>
        </div>
      </div>
      <div class="body-item-3">{{ getFullYear(shipment.created_at) }}</div>
      <div class="body-item-4">{{ shipment.id }}</div>
      <div class="body-item-5">
        <div
          class="toggle flex items-center justify-around w-10 h-10 bg-neutral7 dark:bg-dmGray3 rounded-full cursor-pointer"
          @click="toggleOpenRow(shipment)">
          <i
            class="fa-solid"
            :class="{
              'fa-chevron-up text-brand-primary': openedPackages.includes(shipment),
              'fa-chevron-down': !openedPackages.includes(shipment),
            }" />
        </div>
      </div>
    </div>

    <div class="body-item-collapsible" :class="{ '!hidden': !openedPackages.includes(shipment) }">
      <div
        class="collapsible-image"
        :style="{ backgroundImage: `url('${shipment.media[0]}')`, backgroundSize: 'cover' }"
        @click="showGalleryModal(shipment)">
        <div
          v-if="shipment.media.length > 1"
          class="flex items-center justify-center bg-black h-full w-full bg-opacity-20 rounded-[0.5rem]">
          <span class="text-lg font-bold text-white">{{ `+${shipment.media.length}` }}</span>
        </div>
      </div>

      <AdditionalServicesDropdown class="mb-1" :item="shipment" />

      <ShipmentInspectPackage
        :shipment="shipment"
        @handle-open-services-results-modal="handleOpenServicesResultsModal" />

      <ShipmentExtraPhotos :shipment="shipment" @handle-open-services-results-modal="handleOpenServicesResultsModal" />

      <ShipmentStorageFee :shipment="shipment" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PackageInterface } from '~/types/shipzeePro/packagesTypes'

import { getFullYear } from '~/utils/date/parseDate'
import PackageAdditionalServiceEnum from '~/enums/PackageAdditionalServiceEnum'

const { t } = useLang()

const props = defineProps<{
  shipment: PackageInterface
  openedPackages: PackageInterface[]
}>()

const emit = defineEmits(['handleOpenServicesResultsModal', 'showGalleryModal', 'toggleOpenRow'])

function handleOpenServicesResultsModal(shipment: PackageInterface): void {
  emit('handleOpenServicesResultsModal', shipment)
}

function showGalleryModal(shipment: PackageInterface): void {
  emit('showGalleryModal', shipment)
}

function toggleOpenRow(shipment: PackageInterface): void {
  emit('toggleOpenRow', shipment)
}

const getNotProcessedServiceTranslation = computed<string | null>(() => {
  const notProcessedService = props.shipment.additional_services.filter((service) => !service.completed)[0]

  if (!notProcessedService) {
    return null
  }

  let serviceNameTranslation: string

  switch (notProcessedService.service_id) {
    case PackageAdditionalServiceEnum.InspectPackage:
      serviceNameTranslation = t('site_pro.additional_services.inspect_package.title')
      break
    case PackageAdditionalServiceEnum.ExtraPhotos:
      serviceNameTranslation = t('site_pro.additional_services.extra_photos.title')
      break
    case PackageAdditionalServiceEnum.ReturnToSender:
      serviceNameTranslation = t('site_pro.additional_services.return_to_sender.title')
      break
    case PackageAdditionalServiceEnum.AbandonPackage:
      serviceNameTranslation = t('site_pro.additional_services.abandon_package.title')
      break
    default:
      return null
  }

  return t('site_pro.table.statuses.request_being_processed', {
    service_name: serviceNameTranslation,
  })
})
</script>

<style lang="postcss" scoped>
.in-review-shipments-table-row-component {
  @apply flex flex-col w-full;

  .collapsible-image {
    @apply flex w-20 h-20 min-w-[5rem] min-h-[5rem] rounded-[0.5rem];
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center center;
  }
}
</style>

<style lang="postcss" scoped>
.wl .in-review-shipments-table-row-component {
  @apply bg-white;

  .toggle {
    @apply bg-brand-primaryLight;
  }
}
</style>
